import * as z from "zod";
import { isoDate } from "./common";
import { BREAK_CATEGORIES } from "./live";
import { MarketEstimate } from "./market";

export const SEALED_FORMATS = [
  "HOBBY_BOX",
  "BLASTER_BOX",
  "HANGER_BOX",
  "MEGA_BOX",
  "CASE",
  "TIN",
  "BOOSTER_BOX",
  "OTHER",
] as const;
export type SealedFormat = (typeof SEALED_FORMATS)[number];

/** A box, case or tin the shop breaks or sells unopened, with its stock summed across lots. */
export const SealedProduct = z.object({
  id: z.string(),
  storeId: z.string(),
  status: z.enum(["ACTIVE", "ARCHIVED"]),
  name: z.string(),
  brand: z.string().nullable(),
  year: z.number().nullable(),
  setName: z.string().nullable(),
  category: z.enum(BREAK_CATEGORIES),
  format: z.enum(SEALED_FORMATS),
  imageUrl: z.string().nullable(),
  /** The shop's asking price per unit, in cents; not `marketEstimate`. The break calculator's default. */
  marketValueCents: z.number().nullable(),
  barcode: z.string().nullable(),
  notes: z.string().nullable(),
  /** What the shop paid per unit, from the newest lot that recorded one. */
  costCents: z.number().nullable(),
  onHand: z.number(),
  /** Held by anything: quotes, cart lines and live breaks. */
  reserved: z.number(),
  reservedByBreaks: z.number().default(0),
  reservedByCarts: z.number().default(0),
  available: z.number(),
  trackStock: z.boolean().default(true),
  /** The listing buyers can cart without a scan; null while it is off sale. */
  publicOffer: z.object({ priceCents: z.number() }).nullish(),
  /** The shared market estimate; null when no pricing source knows the product. */
  marketEstimate: MarketEstimate.nullish(),
  createdAt: isoDate,
  updatedAt: isoDate.optional(),
});
export type SealedProduct = z.output<typeof SealedProduct>;

export type SealedProductListFilter = "all" | "in-stock" | "archived" | "linkable";

export interface SealedProductFields {
  brand?: string;
  year?: number;
  setName?: string;
  category?: SealedProduct["category"];
  format?: SealedFormat;
  /** S3 key of an image already uploaded. */
  imageUrl?: string;
  marketValueCents?: number;
  barcode?: string;
  notes?: string;
}

export interface NewSealedProduct extends SealedProductFields {
  name: string;
  /** Units on hand to start with. */
  quantity?: number;
  /** What the shop paid per unit, in cents. */
  costCents?: number;
  locationId?: string;
}

export interface SealedProductPatch extends SealedProductFields {
  name?: string;
}

/** The new total on hand. `costCents` is recorded on a lot the count creates. */
export interface SealedStockUpdate {
  quantity: number;
  kind?: "ADJUSTED" | "SHRINKAGE";
  reason?: string;
  locationId?: string;
  costCents?: number;
}
